let baseUrlWebworker: string = null;
// 
const defaultNamespace: string = import.meta.env.VITE_SERVER_ENV || 'dev';

//todo: delete once moving to APP_DOMAIN fully
export const APP_DOMAIN_OLD = 'geoquant.com';
export const STAGING_DOMAIN = 'stg-v135-pub.stg.bmigeorisksignals.com'
export const APP_DOMAIN = 'bmigeorisksignals.com';

// Production domains the web app is served on. During the rebrand transition the
// app must work on both the old and the new domain, so every hostname-based check
// should reference this list instead of hard-coding a single domain.
export const APP_DOMAINS = [APP_DOMAIN_OLD, APP_DOMAIN];

export const SUPPORT_EMAIL = 'fscustomersupport@fitchsolutions.com'

export function isHeadlessMode(): boolean {
    return (window as any).__headlessGQFlag === true;
}

export function getConnectionString(): {
    api: string;
    ws: string;
    path?: { path: string };
    unauthenticated?: string;
} {
    if(isInternal()){
        const namespace = getInternalNamespace();
        return {
            unauthenticated: `http://webapp-backend-server.${namespace}.svc.cluster.local`,
            api: `http://webapp-backend-server.${namespace}.svc.cluster.local/webapp_api`,
            ws: `http://webapp-backend-server.${namespace}.svc.cluster.local`,
            path: { path: '/api/socket.io' },
        }
    }

    if (!isLocalhost() && !isLocalDNSMapping()) {
        return {
            api: '/api/webapp_api',
            unauthenticated: '/api',
            ws: `https://${document.location.host}`,
            path: { path: '/api/socket.io' },
        };
    }

    if (!defaultNamespace) {
        return {
            unauthenticated: 'http://localhost:8000',
            api: 'http://localhost:8000/webapp_api',
            ws: 'http://localhost:8000',
        };
    }

    const backendURL = getBackendURLForLocalMode();
    return {
        api: `${backendURL}/api/webapp_api`,
        unauthenticated: `${backendURL}/api`,
        ws: backendURL,
        path: { path: '/api/socket.io' },
    };
}

function getBackendURLForLocalMode(){
    if(defaultNamespace === "production"){
        return `https://app.${APP_DOMAIN_OLD}`
    }
    if(defaultNamespace === "legacy-v4"){
        return `https://${defaultNamespace}.${APP_DOMAIN_OLD}`
    }
    return `https://app-${defaultNamespace}.${STAGING_DOMAIN}`

}
export function setBaseURL(url: string) {
    baseUrlWebworker = url;
}
export function getApiURL(): string {
    if (baseUrlWebworker !== null) {
        return baseUrlWebworker;
    }
    return getConnectionString().api;
}

export function getAPIWSUrl(): string {
    if (baseUrlWebworker !== null) {
        return baseUrlWebworker;
    }
    return getConnectionString().ws;
}

export function getAPIWSConfig() {
    const { path } = getConnectionString();
    return path ? path : {};
}

export async function isDebugMode() {
    return false;
}

export function isLocalhost(): boolean {
    return window.location.host.indexOf('localhost') > -1;
}

export function isInternal(): boolean {
    return window.location.host.indexOf('cluster.local') > -1;
}

export function isEmbed(): boolean {
    return window.location !== window.parent.location;
}

export function isProduction(): boolean {
    const domains = APP_DOMAINS
        .map(d => d.replace(/\./g, '\\.'))
        .join('|');
    return new RegExp(`^[\\w\\d\\-]+\\.(?:${domains})$`, 'i').test(window.location.hostname);
}

export const forceStagingIndicator =
    typeof window !== 'undefined' &&
    /[?&]force_staging_ui=1/.test(window.location.href);

export function isStaging(): boolean {
    return !isProduction() && !isLocalhost() && !isLocalDNSMapping();
}

export function shouldShowStagingIndicator(): boolean {
    return isStaging() || forceStagingIndicator;
}

// Captured once at module load: with HashRouter, query params placed after '#'
// get dropped when react-router rewrites the hash on navigation, so read the
// QA override flag eagerly before any navigation happens.
// todo: delete after deleting old domain.
export const forceOldDomain =
    typeof window !== 'undefined' &&
    /[?&]force_old_domain=1/.test(window.location.href);

export function isOldDomain(): boolean {
    const { hostname } = window.location;
    return hostname === APP_DOMAIN_OLD || hostname.endsWith(`.${APP_DOMAIN_OLD}`);
}

// Domains an in-app `original_url` can point to (used to restore the user's page after
// re-authentication). Covers both prod domains plus the new staging sub-domain (stg-v135-pub.stg.bmigeorisksignals.com);
// new-domain staging already lives under APP_DOMAIN.
const ORIGINAL_URL_DOMAINS = [...APP_DOMAINS, STAGING_DOMAIN];

/**
 * Only ever redirect to one of our own app domains over https. Anything else is
 * treated as an open-redirect attempt and ignored.
 */
export function isAllowedRestoreUrl(candidate: string): boolean {
    let url: URL;
    try {
        url = new URL(candidate);
    } catch {
        return false;
    }
    if (url.protocol !== 'https:') {
        return false;
    }
    return ORIGINAL_URL_DOMAINS.some(
        domain => url.hostname === domain || url.hostname.endsWith(`.${domain}`)
    );
}

/**
 * True when the URL still carries the user's working state, i.e. a HashRouter
 * route (`#/graph?countries=...`) and not just the bare app root. Only such URLs
 * are worth snapshotting before a re-authentication redirect.
 *
 * HashRouter routes always start `#/`, so anything else - a bare `#`, a `#/`
 * with no route, or a fragment an auth hop appended for its own purposes such
 * as `#access_token=...` or `#_=_` - is not a route the user asked for and must
 * not be mistaken for one.
 */
export function hasRestorableViewState(url: string): boolean {
    const hashIndex = url.indexOf('#');
    if (hashIndex === -1) {
        return false;
    }
    const fragment = url.slice(hashIndex + 1);
    if (!fragment.startsWith('/')) {
        return false;
    }
    return fragment.replace(/^\/+/, '').length > 0;
}

export function getOriginalUrlFromHref(href: string): string | null {
    const domains = ORIGINAL_URL_DOMAINS
        .map(d => d.replace(/\./g, '\\.'))
        .join('|');
    const match = href.match(new RegExp(`original_url=(https:\\/\\/.*\\.(?:${domains}))`));
    return match ? match[1] : null;
}

export function getInternalNamespace() {
    return document.location.host.match(/([\w-]+)\.svc/)[1];
}

export function isLocalDNSMapping(): boolean {
    return window.location.host.indexOf('app-dev-local') > -1;
}


export function getUserFacingUrl() {
    return isInternal()
        ? `https://app-${getInternalNamespace()}.${STAGING_DOMAIN}`
        : window.location.origin;
}

